import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Type, Square, Upload, Trash2, Loader2, AlignLeft, AlignCenter, AlignRight } from "lucide-react";
import { useCustomFonts, uploadFont, deleteFont, TELUGU_FONTS, isTeluguReady, teluguMatch } from "@/lib/fonts";
import { isFeatureEnabled } from "@/lib/features";
import { api } from "@/lib/api";

export const STYLE_FONTS = ["Inter", "Montserrat", "Poppins", "Roboto", "Anton", "Bebas Neue", "Oswald", "Arial", "Helvetica", "Georgia", "Times New Roman"];
export const STYLE_ANIMS = ["pop", "bouncy", "fade", "slide-up", "slide-down", "slide-left", "slide-right", "typewriter", "kinetic", "punch", "minimal", "motion-blur"];

function Color({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="mb-1 text-[11px] text-[#A1A1AA]">{label}</div>
      <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5">
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0" />
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent font-plex text-xs uppercase outline-none" />
      </div>
    </div>
  );
}

function Row({ label, val, children }) {
  return (
    <div className="mb-2">
      <div className="mb-1 flex justify-between text-[11px] text-[#A1A1AA]"><span>{label}</span><span className="tabular-nums text-white">{val}</span></div>
      {children}
    </div>
  );
}

function Group({ title, enabled, onToggle, testid, children }) {
  return (
    <div className="mb-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <label className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-white">{title}</span>
        <Switch checked={!!enabled} onCheckedChange={onToggle} data-testid={testid} />
      </label>
      <div data-ws-panel="" className={enabled ? "mt-3" : "mt-3 hidden"}>{children}</div>
    </div>
  );
}

export default function StyleEditor({ value, onPatch, showFont = false, prefix = "style" }) {
  const [tab, setTab] = useState("text");
  const s = value;
  const set = (k) => (v) => onPatch({ [k]: v });
  const customFonts = useCustomFonts();
  const fileRef = useRef(null);
  const [fontBusy, setFontBusy] = useState(false);
  const [gradients, setGradients] = useState([]);
  useEffect(() => {
    if (isFeatureEnabled("popular_gradients")) api.gradients().then((d) => setGradients(d.gradients || [])).catch(() => {});
  }, []);
  const applyPopularGradient = (g) => onPatch({
    fillGradientEnabled: true, fillGradientFrom: g.from_color, fillGradientTo: g.to_color,
    fillGradientAngle: g.angle ?? 90,
  });

  const onUploadFont = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setFontBusy(true);
    try {
      const doc = await uploadFont(f);
      set("fontFamily")(doc.family);
      if (doc.glyph_warning) toast.warning(doc.glyph_warning);
      else toast.success(`Font "${doc.family}" added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't upload that font.");
    }
    setFontBusy(false);
  };

  return (
    <div data-testid={`${prefix}-editor`}>
      <div className="mb-3 flex gap-1 rounded-lg border border-white/10 bg-white/[0.02] p-0.5">
        {[{ k: "text", l: "Text", i: Type }, { k: "background", l: "Background", i: Square }].map((tt) => (
          <button key={tt.k} data-testid={`${prefix}-tab-${tt.k}`} onClick={() => setTab(tt.k)}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-semibold transition-colors ${tab === tt.k ? "bg-[#00F0FF] text-black" : "text-[#A1A1AA] hover:text-white"}`}>
            <tt.i size={13} /> {tt.l}
          </button>
        ))}
      </div>


        <div data-ws-panel="" className={tab === "text" ? "" : "hidden"}>
          {showFont && (
            <div className="mb-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <div className="mb-2 grid grid-cols-2 gap-2">
                <select data-testid={`${prefix}-font-family`} value={s.fontFamily} onChange={(e) => set("fontFamily")(e.target.value)} className="rounded-lg border border-white/10 bg-[#1a1a1a] px-2 py-2 text-sm outline-none">
                  {STYLE_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                  <optgroup label="Telugu-ready fonts">
                    {TELUGU_FONTS.map((f) => <option key={f} value={f}>{f} · తెలుగు</option>)}
                  </optgroup>
                  {customFonts.length > 0 && (
                    <optgroup label="Your fonts">
                      {customFonts.map((f) => <option key={f.id} value={f.family}>{f.family}{f.has_telugu ? " · తెలుగు" : ""}</option>)}
                    </optgroup>
                  )}
                </select>
                <select data-testid={`${prefix}-font-weight`} value={s.fontWeight} onChange={(e) => set("fontWeight")(parseInt(e.target.value))} className="rounded-lg border border-white/10 bg-[#1a1a1a] px-2 py-2 text-sm outline-none">
                  {[300, 400, 500, 600, 700, 800, 900].map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
              <div className="mb-2">
                <input ref={fileRef} type="file" accept=".ttf,.otf,.woff" className="hidden" onChange={onUploadFont} data-testid={`${prefix}-font-upload-input`} />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={fontBusy} data-testid={`${prefix}-font-upload-btn`}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] py-2 text-xs font-semibold text-white transition-colors hover:border-[#00F0FF]/50 disabled:opacity-60">
                  {fontBusy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload font (TTF / OTF / WOFF)
                </button>
                {customFonts.length > 0 && (
                  <div className="mt-1.5 space-y-1">
                    {customFonts.map((f) => (
                      <div key={f.id} className="flex items-center justify-between rounded-md bg-white/[0.03] px-2 py-1 text-[11px]" data-testid={`custom-font-${f.id}`}>
                        <span className="truncate" style={{ fontFamily: `"${f.family}"` }}>{f.family}{f.has_telugu ? <span className="ml-1 text-[#00F0FF]" title="Has Telugu letters">తె</span> : <span className="ml-1 text-[#f59e0b]" title="No Telugu letters — Telugu words use a matching Telugu font">⚠</span>}</span>
                        <button type="button" onClick={() => deleteFont(f.id)} data-testid={`custom-font-delete-${f.id}`} className="text-[#A1A1AA] hover:text-red-400"><Trash2 size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="mb-2 text-[10px] text-[#A1A1AA]" data-testid={`${prefix}-telugu-font-hint`}>
                {isTeluguReady(s.fontFamily) ? <span className="text-[#00F0FF]">తె This font has Telugu letters — Telugu words use it.</span>
                  : <>Telugu words use the matching <span className="font-semibold text-white">{teluguMatch(s.fontFamily)}</span> font.</>}
              </div>
              <Row label="Font size" val={Math.round(s.fontSize)}><Slider data-testid={`${prefix}-font-size`} value={[s.fontSize]} min={40} max={200} step={2} onValueChange={([v]) => set("fontSize")(v)} /></Row>
              <Row label="Opacity" val={`${Math.round((s.opacity ?? 1) * 100)}%`}><Slider data-testid={`${prefix}-opacity`} value={[s.opacity ?? 1]} min={0} max={1} step={0.05} onValueChange={([v]) => set("opacity")(v)} /></Row>
              <div className="mt-1">
                <div className="mb-1 text-[11px] text-[#A1A1AA]">Animation</div>
                <select data-testid={`${prefix}-animation`} value={s.animation} onChange={(e) => set("animation")(e.target.value)} className="w-full rounded-lg border border-white/10 bg-[#1a1a1a] px-2 py-2 text-sm outline-none">
                  {STYLE_ANIMS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
          )}

          <div className="mb-3 rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={`${prefix}-typography`}>
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-white">Typography</div>
            {isFeatureEnabled("text_alignment") && (
              <div className="mb-2">
                <div className="mb-1 text-[11px] text-[#A1A1AA]">Alignment</div>
                <div className="flex gap-1 rounded-lg border border-white/10 bg-white/[0.02] p-0.5">
                  {[{ v: "left", I: AlignLeft }, { v: "center", I: AlignCenter }, { v: "right", I: AlignRight }].map(({ v, I }) => (
                    <button key={v} type="button" data-testid={`${prefix}-align-${v}`} onClick={() => set("align")(v)}
                      className={`flex flex-1 items-center justify-center rounded-md py-1.5 transition-colors ${(s.align || "center") === v ? "bg-[#00F0FF] text-black" : "text-[#A1A1AA] hover:text-white"}`}>
                      <I size={14} />
                    </button>
                  ))}
                </div>
              </div>
            )}
            {isFeatureEnabled("text_case") && (
              <div className="mb-2">
                <div className="mb-1 text-[11px] text-[#A1A1AA]">Text case</div>
                <select data-testid={`${prefix}-text-case`} value={s.textCase || "none"} onChange={(e) => set("textCase")(e.target.value)}
                  className="w-full rounded-lg border border-white/10 bg-[#1a1a1a] px-2 py-2 text-sm outline-none">
                  <option value="none">Original</option>
                  <option value="upper">UPPERCASE</option>
                  <option value="lower">lowercase</option>
                  <option value="title">Title Case</option>
                  <option value="sentence">Sentence case</option>
                </select>
              </div>
            )}
            {isFeatureEnabled("letter_spacing") && (
              <Row label="Letter spacing" val={(s.letterSpacing ?? 0)}>
                <div className="flex items-center gap-2">
                  <Slider data-testid={`${prefix}-letter-spacing`} value={[s.letterSpacing ?? 0]} min={-10} max={40} step={1} onValueChange={([v]) => set("letterSpacing")(v)} />
                  <button type="button" data-testid={`${prefix}-letter-spacing-reset`} onClick={() => set("letterSpacing")(0)} className="rounded border border-white/10 px-1.5 py-0.5 text-[10px] text-[#A1A1AA] hover:text-white">Reset</button>
                </div>
              </Row>
            )}
          </div>

          <div className="mb-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-white">Fill</div>
            <Color label="Solid colour" value={s.fillColor} onChange={set("fillColor")} testid={`${prefix}-fill`} />
            <label className="mt-3 flex items-center justify-between">
              <span className="text-[11px] font-semibold text-[#A1A1AA]">Gradient fill</span>
              <Switch checked={!!s.fillGradientEnabled} onCheckedChange={set("fillGradientEnabled")} data-testid={`${prefix}-gradient-toggle`} />
            </label>
            {s.fillGradientEnabled && (
              <div className="mt-2">
                {isFeatureEnabled("popular_gradients") && gradients.length > 0 && (
                  <div className="mb-2">
                    <div className="mb-1 text-[11px] text-[#A1A1AA]">Popular</div>
                    <div className="grid grid-cols-5 gap-1.5" data-testid={`${prefix}-popular-gradients`}>
                      {gradients.map((g) => {
                        const active = s.fillGradientFrom === g.from_color && s.fillGradientTo === g.to_color;
                        return (
                          <button key={g.id} type="button" title={g.name} data-testid={`${prefix}-gradient-${g.id}`} onClick={() => applyPopularGradient(g)}
                            className={`h-8 rounded-md border transition-transform hover:scale-105 ${active ? "border-[#00F0FF] ring-1 ring-[#00F0FF]" : "border-white/10"}`}
                            style={{ backgroundImage: `linear-gradient(${g.angle ?? 90}deg, ${g.from_color}, ${g.to_color})` }} />
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Color label="From" value={s.fillGradientFrom} onChange={set("fillGradientFrom")} testid={`${prefix}-gradient-from`} />
                  <Color label="To" value={s.fillGradientTo} onChange={set("fillGradientTo")} testid={`${prefix}-gradient-to`} />
                </div>
                <Row label="Angle" val={`${Math.round(s.fillGradientAngle ?? 90)}°`}><Slider data-testid={`${prefix}-gradient-angle`} value={[s.fillGradientAngle ?? 90]} min={0} max={360} step={5} onValueChange={([v]) => set("fillGradientAngle")(v)} /></Row>
              </div>
            )}
          </div>

          <Group title="Stroke / Outline" enabled={s.strokeEnabled} onToggle={set("strokeEnabled")} testid={`${prefix}-stroke-toggle`}>
            <div className="mb-2"><Color label="Colour" value={s.strokeColor} onChange={set("strokeColor")} testid={`${prefix}-stroke-color`} /></div>
            <Row label="Width" val={s.strokeWidth}><Slider data-testid={`${prefix}-stroke-width`} value={[s.strokeWidth]} min={0} max={30} step={1} onValueChange={([v]) => set("strokeWidth")(v)} /></Row>
          </Group>

          <Group title="Drop Shadow" enabled={s.shadowEnabled} onToggle={set("shadowEnabled")} testid={`${prefix}-shadow-toggle`}>
            <div className="mb-2"><Color label="Colour" value={s.shadowColor} onChange={set("shadowColor")} testid={`${prefix}-shadow-color`} /></div>
            <div className="grid grid-cols-2 gap-3">
              <Row label="Offset X" val={s.shadowDx}><Slider value={[s.shadowDx]} min={-30} max={30} step={1} onValueChange={([v]) => set("shadowDx")(v)} /></Row>
              <Row label="Offset Y" val={s.shadowDy}><Slider value={[s.shadowDy]} min={-30} max={30} step={1} onValueChange={([v]) => set("shadowDy")(v)} /></Row>
              <Row label="Blur" val={s.shadowBlur}><Slider value={[s.shadowBlur]} min={0} max={30} step={1} onValueChange={([v]) => set("shadowBlur")(v)} /></Row>
              <Row label="Opacity" val={`${Math.round(s.shadowOpacity * 100)}%`}><Slider value={[s.shadowOpacity]} min={0} max={1} step={0.05} onValueChange={([v]) => set("shadowOpacity")(v)} /></Row>
            </div>
          </Group>

          <Group title="Outer Glow" enabled={s.glowEnabled} onToggle={set("glowEnabled")} testid={`${prefix}-glow-toggle`}>
            <div className="mb-2"><Color label="Colour" value={s.glowColor} onChange={set("glowColor")} testid={`${prefix}-glow-color`} /></div>
            <Row label="Blur" val={s.glowBlur}><Slider value={[s.glowBlur]} min={0} max={40} step={1} onValueChange={([v]) => set("glowBlur")(v)} /></Row>
            <Row label="Intensity" val={`${Math.round(s.glowOpacity * 100)}%`}><Slider value={[s.glowOpacity]} min={0} max={1} step={0.05} onValueChange={([v]) => set("glowOpacity")(v)} /></Row>
          </Group>

          <Group title="3D Extrude" enabled={s.threeDEnabled} onToggle={set("threeDEnabled")} testid={`${prefix}-threed-toggle`}>
            <div className="mb-2"><Color label="Colour" value={s.threeDColor} onChange={set("threeDColor")} testid={`${prefix}-threed-color`} /></div>
            <Row label="Depth" val={s.threeDDepth}><Slider data-testid={`${prefix}-threed-depth`} value={[s.threeDDepth]} min={0} max={24} step={1} onValueChange={([v]) => set("threeDDepth")(v)} /></Row>
          </Group>
        </div>


      <div data-ws-panel="" className={tab === "background" ? "" : "hidden"}>
        <Group title="Background" enabled={s.bgEnabled} onToggle={set("bgEnabled")} testid={`${prefix}-bg-toggle`}>
          <div className="mb-2 flex gap-1 rounded-lg border border-white/10 bg-white/[0.02] p-0.5">
            {["solid", "gradient"].map((tp) => (
              <button key={tp} data-testid={`${prefix}-bg-type-${tp}`} onClick={() => set("bgType")(tp)}
                className={`flex-1 rounded-md py-1.5 text-xs font-semibold capitalize transition-colors ${s.bgType === tp ? "bg-[#8B5CF6] text-white" : "text-[#A1A1AA] hover:text-white"}`}>{tp}</button>
            ))}
          </div>
          <div className="mb-2 grid grid-cols-2 gap-2">
            <Color label="Colour" value={s.bgColor} onChange={set("bgColor")} testid={`${prefix}-bg-color`} />
            {s.bgType === "gradient" && <Color label="Colour 2" value={s.bgColor2} onChange={set("bgColor2")} testid={`${prefix}-bg-color2`} />}
          </div>
          {s.bgType === "gradient" && <Row label="Angle" val={`${Math.round(s.bgAngle)}°`}><Slider value={[s.bgAngle]} min={0} max={360} step={5} onValueChange={([v]) => set("bgAngle")(v)} /></Row>}
          <Row label="Opacity" val={`${Math.round(s.bgOpacity * 100)}%`}><Slider value={[s.bgOpacity]} min={0} max={1} step={0.05} onValueChange={([v]) => set("bgOpacity")(v)} /></Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Padding X" val={s.bgPaddingX.toFixed(2)}><Slider value={[s.bgPaddingX]} min={0} max={1.5} step={0.05} onValueChange={([v]) => set("bgPaddingX")(v)} /></Row>
            <Row label="Padding Y" val={s.bgPaddingY.toFixed(2)}><Slider value={[s.bgPaddingY]} min={0} max={1.5} step={0.05} onValueChange={([v]) => set("bgPaddingY")(v)} /></Row>
          </div>
          <Row label="Corner radius" val={s.bgRadius.toFixed(2)}><Slider value={[s.bgRadius]} min={0} max={1} step={0.05} onValueChange={([v]) => set("bgRadius")(v)} /></Row>
          <div className="mt-2 flex rounded-lg border border-white/10 p-0.5" data-testid={`${prefix}-bg-mode`}>
            {[["container", "Whole caption"], ["word", "Per word"]].map(([m, lbl]) => (
              <button key={m} data-testid={`${prefix}-bg-mode-${m}`} onClick={() => set("bgMode")(m)}
                className={`flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors ${(s.bgMode || "container") === m ? "bg-[#8B5CF6] text-white" : "text-[#A1A1AA] hover:text-white"}`}>{lbl}</button>
            ))}
          </div>
        </Group>
      </div>
    </div>
  );
}
